import { useState, useEffect, useCallback } from "react"
import axios, { type AxiosResponse } from "axios"
import { enqueueSnackbar } from "notistack"
import { useSelector } from "react-redux"
import { captureException } from "@sentry/nextjs"

import type { RootState } from "@/types/redux"
import { API_ROOT_URL } from "../requests"
import useTranslation from "./use_translation"

type FetchOptions = { useCache?: boolean }
type FetchResult<T> = [T | null, unknown]
type TranslateFn = (key: string) => string

const cachedRequests: Record<string, Promise<AxiosResponse<unknown>>> = {}

const isFullUrl = (path: string): boolean =>
  typeof path === "string" && (path.startsWith("http://") || path.startsWith("https://"))

const makeRequest = (t: TranslateFn, path: string): Promise<AxiosResponse<unknown>> => {
  const url = isFullUrl(path) ? path : `${API_ROOT_URL}${path}`
  const request = axios.get(url)
  request.catch((e) => {
    if (e.response?.status === 401) {
      console.log(`Unauthorized Request: ${path}`)
    } else if (!e.response) {
      console.error(`Network Error: ${path} ${e} ${e.response} ${e.message}`)
      console.error(e)
    } else {
      console.error(`Request Error: ${path} ${e} ${e.response} ${e.message}`)
      console.error(e)
      captureException(e)
      enqueueSnackbar(t("unexpectedError"), { variant: "error", autoHideDuration: 5000 })
    }
  })
  return request
}

const getOrCreateRequest = (t: TranslateFn, path: string, useCache: boolean) => {
  const request = (useCache && cachedRequests[path]) || makeRequest(t, path)
  cachedRequests[path] = request
  return request
}

export const useFetchCallback = <T = unknown>(
  path: string,
  { useCache = false }: FetchOptions = {},
): [() => Promise<FetchResult<T>>, boolean] => {
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState(false)

  const fetch = useCallback(async (): Promise<FetchResult<T>> => {
    setIsFetching(true)
    try {
      const { data } = await getOrCreateRequest(t, path, useCache)
      return [data as T, null]
    } catch (error) {
      return [null, error]
    } finally {
      setIsFetching(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is a static translation function
  }, [path, useCache])

  return [fetch, isFetching]
}

export const useFetch = <T = unknown>(
  path: string,
  options?: FetchOptions,
): [T | null, boolean, unknown, () => void] => {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [fetch, isFetching] = useFetchCallback<T>(path, options)
  const waitForAuth = useSelector((s: RootState) => !s.auth && path.startsWith("/protected"))

  const refresh = useCallback(() => {
    void (async () => {
      delete cachedRequests[path]
      const [nextData, nextError] = await fetch()
      setError(nextError)
      if (!nextError) {
        setData(nextData)
      }
    })()
  }, [fetch, path])

  useEffect(() => {
    if (waitForAuth) {
      return
    }
    refresh()
  }, [refresh, waitForAuth])

  return [data, isFetching, error, refresh]
}
